'use client';

import { useCallback, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation';
import { Icon } from '@/app/common/icons/Icon';
import { useApexCalendarFilters } from './ApexCalendarFiltersProvider';
import { useClickOutside } from './useClickOutside';
import {
  SelectorBodyRow,
  SelectorActions,
  SelectorActionButton,
  SelectorBody,
  SelectorBodyValue,
  SelectorHeader,
  SelectorLabel,
  SelectorRow,
  SelectorValue,
  SelectorBodyCheckRow,
  StyledCheckbox,
} from './apexCalendarSelectors.styles';

const MEAL_PLANS = [
  { value: 1, key: 'vacation.common.mealplantype.onlybed' },
  { value: 2, key: 'vacation.common.mealplantype.bedandbreakfast' },
  { value: 3, key: 'vacation.common.mealplantype.halfboard' },
  { value: 4, key: 'vacation.common.mealplantype.fullboard' },
  { value: 5, key: 'vacation.common.mealplantype.allinclusive' },
] as const;

const NIGHT_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 1).map((n) => ({
  value: n,
}));

export function ApexCalendarCollapsibleFilters({ isExpanded }: { isExpanded: boolean }) {
  const { t } = useTranslation();
  const { nights, mealPlans, setNights, setMealPlans } = useApexCalendarFilters();
  const [nightsOpen, setNightsOpen] = useState(false);
  const [mealOpen, setMealOpen] = useState(false);
  const [draftMealPlans, setDraftMealPlans] = useState<number[]>(mealPlans);
  const nightsRef = useClickOutside<HTMLDivElement>(() => setNightsOpen(false));

  const handleMealCancel = useCallback(() => {
    setDraftMealPlans(mealPlans);
    setMealOpen(false);
  }, [mealPlans]);

  const mealRef = useClickOutside<HTMLDivElement>(handleMealCancel);

  if (!isExpanded) return null;

  const nightLabel = (n: number) => `${n} ${n === 1 ? t('vacation.common.night') : t('vacation.common.nights')}`;
  const nightsLabel = nightLabel(nights);
  const mealLabel =
    mealPlans.length > 0
      ? mealPlans.map((v) => t(MEAL_PLANS.find((m) => m.value === v)!.key)).join(', ')
      : t('vacation.oss.components.searchfilters.filters.meal.any');

  const toggleMealOpen = () => {
    if (!mealOpen) {
      setDraftMealPlans(mealPlans);
    }
    setMealOpen((open) => !open);
  };

  const toggleDraftMealPlan = (value: number) => {
    setDraftMealPlans((current) =>
      current.includes(value) ? current.filter((selectedValue) => selectedValue !== value) : [...current, value]
    );
  };

  const handleMealDone = () => {
    setMealPlans(draftMealPlans);
    setMealOpen(false);
  };

  return (
    <>
      <SelectorRow ref={nightsRef}>
        <SelectorHeader $isOpen={nightsOpen} onClick={() => setNightsOpen((o) => !o)}>
          <SelectorLabel>{t('vacation.select.evo_price_calendar.how_long') || 'How long'}</SelectorLabel>
          <SelectorValue>{nightsLabel}</SelectorValue>
        </SelectorHeader>
        {nightsOpen && (
          <SelectorBody>
            {NIGHT_OPTIONS.map(({ value }) => (
              <SelectorBodyRow key={value} onClick={() => setNights(value)} style={{ cursor: 'pointer' }}>
                <SelectorBodyValue>{nightLabel(value)}</SelectorBodyValue>
                {nights === value && (
                  <Icon category="Semantics" name="icon-check" size={20} color="iconPrimary" alt="" />
                )}
              </SelectorBodyRow>
            ))}
          </SelectorBody>
        )}
      </SelectorRow>

      <SelectorRow ref={mealRef}>
        <SelectorHeader $isOpen={mealOpen} onClick={toggleMealOpen}>
          <SelectorLabel>{t('vacation.oss.components.searchfilters.group.meal')}</SelectorLabel>
          <SelectorValue>{mealLabel}</SelectorValue>
        </SelectorHeader>
        {mealOpen && (
          <SelectorBody>
            {MEAL_PLANS.map(({ value, key }) => (
              <SelectorBodyCheckRow key={value}>
                <StyledCheckbox
                  checkbox={{ id: `meal-${value}`, label: t(key) }}
                  labelPosition="end"
                  isChecked={draftMealPlans.includes(value)}
                  onChange={() => toggleDraftMealPlan(value)}
                />
              </SelectorBodyCheckRow>
            ))}
            <SelectorActions>
              <SelectorActionButton variant="primary" appearance="ghost" onClick={handleMealCancel}>
                {t('vacation.oss.components.peopleselector.cancel')}
              </SelectorActionButton>
              <SelectorActionButton variant="primary" onClick={handleMealDone}>
                {t('vacation.oss.components.peopleselector.done')}
              </SelectorActionButton>
            </SelectorActions>
          </SelectorBody>
        )}
      </SelectorRow>
    </>
  );
}
